/**
 * Authentication endpoints.
 *
 * Typed against the backend's `/auth/` surface. The snake_case → camelCase
 * conversion happens here, at the edge, so nothing downstream has to remember
 * which convention it is looking at.
 */

import { api } from "@/api/client";
import type { Session } from "@/repositories/authRepository";

interface PrincipalResponse {
  access?: string;
  refresh?: string;
  user: {
    id: string;
    staff_code: string;
    display_name: string;
    email: string | null;
    is_platform_staff: boolean;
    is_personal?: boolean;
    is_demo?: boolean;
    phone?: string;
    must_change_password: boolean;
    must_set_pin: boolean;
  };
  merchant: {
    id: string;
    name: string;
    vat_mode: "NONE" | "INCLUSIVE" | "EXCLUSIVE";
    vat_rate: string;
    currency: string;
  } | null;
  scope: {
    all_branches: boolean;
    all_sections: boolean;
    branch_ids: string[];
    section_ids: string[];
  };
  roles: string[];
  permissions: string[];
  discount_limit_percent: string;
  device: {
    id: string;
    name: string;
    branch_id: string;
    till_id: string | null;
  } | null;
  support_view?: { expires_at: string; reason: string } | null;
}

function toSession(
  body: PrincipalResponse,
  tokens: { access: string; refresh: string },
): Session {
  return {
    key: "current",
    accessToken: tokens.access,
    refreshToken: tokens.refresh,
    user: {
      id: body.user.id,
      staffCode: body.user.staff_code,
      displayName: body.user.display_name,
      email: body.user.email,
      isPlatformStaff: body.user.is_platform_staff,
      isPersonal: body.user.is_personal ?? false,
      isDemo: body.user.is_demo ?? false,
      phone: body.user.phone ?? "",
      mustSetPin: body.user.must_set_pin,
      mustChangePassword: body.user.must_change_password,
    },
    merchant: body.merchant
      ? {
          id: body.merchant.id,
          name: body.merchant.name,
          vatMode: body.merchant.vat_mode,
          vatRate: body.merchant.vat_rate,
          currency: body.merchant.currency,
        }
      : null,
    scope: {
      allBranches: body.scope.all_branches,
      allSections: body.scope.all_sections,
      branchIds: body.scope.branch_ids,
      sectionIds: body.scope.section_ids,
    },
    roles: body.roles,
    permissions: body.permissions,
    discountLimitPercent: body.discount_limit_percent,
    device: body.device
      ? {
          id: body.device.id,
          name: body.device.name,
          branchId: body.device.branch_id,
          tillId: body.device.till_id,
        }
      : null,
    // Null means "my own business" — the server's default, and the answer for
    // everybody who belongs to exactly one.
    activeMerchantId: null,
    supportView: body.support_view
      ? { expiresAt: body.support_view.expires_at, reason: body.support_view.reason }
      : null,
    activeBranchId: body.device?.branch_id ?? null,
    activeSectionId: null,
    signedInAt: new Date().toISOString(),
  };
}

export const authApi = {
  /** Email and password. Owners, managers and platform staff. */
  async loginWithPassword(email: string, password: string): Promise<Session> {
    const body = await api.post<PrincipalResponse>("/auth/login/", { email, password });
    return toSession(body, { access: body.access!, refresh: body.refresh! });
  },

  /**
   * Staff code and PIN, from a paired terminal.
   *
   * The device token travels in a header rather than the body so it is never
   * captured as request data in a log.
   */
  async loginWithPin(
    staffCode: string,
    pin: string,
    deviceToken: string,
  ): Promise<Session> {
    const body = await api.post<PrincipalResponse>(
      "/auth/pin/verify/",
      { staff_code: staffCode, pin },
      // The terminal's token authenticates this, not a user — nobody is signed
      // in yet. A 401 here is a wrong PIN or a revoked pairing, neither of which
      // is a user session ending.
      { headers: { "X-SmartSpaza-Device-Token": deviceToken }, authenticatedByDevice: true },
    );
    return toSession(body, { access: body.access!, refresh: body.refresh! });
  },

  /**
   * Cellphone sign-in, step one: text a one-time PIN.
   *
   * Says nothing about whether the number has an account — the server answers
   * the same either way.
   */
  async sendPhoneCode(phone: string): Promise<void> {
    await api.post("/auth/phone/code/", { phone });
  },

  /**
   * Step two: the PIN. A number with an account is signed in; a new one gets
   * a registration token to finish signing up with.
   */
  async verifyPhoneCode(
    phone: string,
    code: string,
  ): Promise<{ session: Session } | { registrationToken: string }> {
    const body = await api.post<PrincipalResponse & { registration_token?: string }>(
      "/auth/phone/verify/",
      { phone, code },
    );
    if (body.registration_token) return { registrationToken: body.registration_token };
    return { session: toSession(body, { access: body.access!, refresh: body.refresh! }) };
  },

  /** Step three, for a new number: a name and the Terms. */
  async registerWithPhone(details: {
    registrationToken: string;
    firstName: string;
    lastName: string;
    email: string;
    acceptedTerms: boolean;
  }): Promise<Session> {
    const body = await api.post<PrincipalResponse>("/auth/phone/register/", {
      registration_token: details.registrationToken,
      first_name: details.firstName,
      last_name: details.lastName,
      email: details.email,
      accepted_terms: details.acceptedTerms,
    });
    return toSession(body, { access: body.access!, refresh: body.refresh! });
  },

  /** Rehydrate on app start. Confirms the token is still good and the scope current. */
  async me(existing: { access: string; refresh: string }): Promise<Session> {
    const body = await api.get<PrincipalResponse>("/auth/me/");
    return toSession(body, existing);
  },

  async refresh(refreshToken: string): Promise<{ access: string; refresh: string }> {
    const body = await api.post<{ access: string; refresh?: string }>("/auth/refresh/", {
      refresh: refreshToken,
    });
    // Rotation is enabled server-side, so a new refresh token normally comes
    // back. Falling back to the old one keeps this working if that changes.
    return { access: body.access, refresh: body.refresh ?? refreshToken };
  },

  /** Blacklists the refresh token, so signing out on a shared terminal really ends it. */
  async logout(refreshToken: string): Promise<void> {
    await api.post("/auth/logout/", { refresh: refreshToken });
  },

  async setPin(pin: string): Promise<void> {
    await api.post("/auth/pin/set/", { pin });
  },
};
